import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const PJS = "'Plus Jakarta Sans', 'Nunito Sans', sans-serif";

export default function PromoPopup() {
  const { content, openBooking } = useSite();
  const [show, setShow] = useState(false);
  const promo = content.promo || {};

  useEffect(() => {
    if (promo.enabled === false || !promo.headline) return;
    if (sessionStorage.getItem("td_promo_shown")) return;

    const trigger = () => {
      sessionStorage.setItem("td_promo_shown", "1");
      setShow(true);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("mouseleave", onLeave);
    };
    const onOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };
    const onLeave = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };

    document.addEventListener("mouseout", onOut);
    document.addEventListener("mouseleave", onLeave);
    return () => {
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [promo.enabled, promo.headline]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-[rgba(4,9,26,0.8)] backdrop-blur-sm"
          onClick={() => setShow(false)}
          data-testid="promo-popup-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 44, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-3xl border border-[rgba(0,207,255,0.55)] overflow-hidden text-white"
            style={{
              background: "rgba(9, 18, 36, 0.78)",
              backdropFilter: "blur(22px)",
              boxShadow: "0 0 0 1px rgba(0,207,255,0.25), 0 0 52px rgba(0,191,255,0.32), 0 34px 90px rgba(0,0,0,0.65)",
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="promo-popup"
          >
            <div className="glow-blob" style={{ width: 260, height: 260, top: -120, right: -80, background: "radial-gradient(circle, rgba(0,191,255,0.35), transparent 65%)", animation: "drift1 10s ease-in-out infinite" }} />
            <button onClick={() => setShow(false)} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/10 transition-colors z-10" aria-label="Close promotion" data-testid="promo-popup-close">
              <X size={17} />
            </button>
            <div className="p-8 sm:p-10 text-center">
              <div
                className="eyebrow text-[10px] text-[#00CFFF] uppercase tracking-[0.28em]"
                style={{ fontFamily: PJS, fontWeight: 700 }}
                data-testid="promo-label"
              >
                {promo.label || "Only a few spots left this week"}
              </div>
              <h3
                className="promo-headline-font mt-4 italic font-extrabold text-white text-3xl sm:text-4xl leading-tight"
                data-testid="promo-headline"
              >
                {promo.headline}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-300" style={{ fontFamily: PJS, fontWeight: 500 }} data-testid="promo-message">
                {promo.message}
              </p>
              <button
                className="btn btn-primary w-full justify-center mt-8"
                style={{ fontFamily: PJS, fontWeight: 700, boxShadow: "0 16px 38px rgba(0,80,200,0.5), 0 4px 16px rgba(0,0,0,0.45)" }}
                onClick={() => { setShow(false); openBooking(null, "booking"); }}
                data-testid="promo-popup-cta"
              >
                {promo.cta_text || "Book a Free Consultation"} <ArrowRight size={16} strokeWidth={2.5} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
